import { Alert, Box, Skeleton, Tooltip, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useIntl } from "../../contexts/IntlContext";
import StatusPill from "../ui/StatusPill";
import { fontWeights } from "../../themes/fontWeights";

type StatusLevel = "ok" | "alert";

interface StatusDetail {
  label: string;
  level: StatusLevel;
  adornment?: ReactNode;
  tooltip?: string;
}

export interface CameraStatus {
  id: string;
  establishmentId: string;
  deviceId: number;
  name: string;
  cameraStatus: "online" | "offline";
  lastEvent: StatusDetail;
  sdCardStatus: StatusDetail;
  buttonStatus: StatusDetail;
}

interface CameraStatusTableProps {
  cameras: CameraStatus[];
  headerRight?: ReactNode;
  loading?: boolean;
  error?: string | null;
}

const CameraStatusTable = ({ cameras, headerRight, loading = false, error = null }: CameraStatusTableProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const borderColor = theme.palette.divider;
  const surfaceColor = theme.palette.background.paper;
  const headerBg = alpha(theme.palette.background.paper, 0.8);
  // Listra sutil derivada do texto: funciona nos dois temas sem esmaecer o conteúdo da linha.
  const evenRowBg = alpha(theme.palette.text.primary, 0.035);
  const rowHoverBg = alpha(theme.palette.text.primary, 0.07);

  const statusColors = {
    // Estado normal em tom neutro: só o que exige atenção ganha cor.
    ok: {
      text: theme.palette.text.secondary,
    },
    alert: {
      text: theme.palette.error.main,
    },
  } as const;

  const renderCameraPill = (status: "online" | "offline") => {
    const isOnline = status === "online";

    return (
      <StatusPill
        level={isOnline ? "success" : "error"}
        label={intl.formatMessage({ id: isOnline ? "dashboard.cameraOnline" : "dashboard.cameraOffline" })}
      />
    );
  };

  const renderDetail = (detail: StatusDetail) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, maxWidth: "100%" }}>
      {detail.adornment ? (
        detail.tooltip ? (
          <Tooltip title={detail.tooltip}>
            <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
              {detail.adornment}
            </Box>
          </Tooltip>
        ) : (
          <Box component="span" sx={{ display: "inline-flex", alignItems: "center" }}>
            {detail.adornment}
          </Box>
        )
      ) : null}
      <Tooltip title={detail.level === "alert" ? detail.label : ""}>
        <Typography
          component="span"
          variant="body2"
          sx={{
            color: statusColors[detail.level].text,
            fontWeight: detail.level === "alert" ? 600 : 400,
            minWidth: 0,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {detail.label}
        </Typography>
      </Tooltip>
    </Box>
  );

  const headers = [
    intl.formatMessage({ id: "dashboard.cameraName" }),
    intl.formatMessage({ id: "dashboard.cameraStatusColumn" }),
    intl.formatMessage({ id: "dashboard.cameraLastEvent" }),
    intl.formatMessage({ id: "dashboard.cameraSdStatus" }),
    intl.formatMessage({ id: "dashboard.cameraButtonStatus" }),
  ];

  return (
    <Box
      sx={{
        backgroundColor: surfaceColor,
        borderRadius: 1,
        border: `1px solid ${borderColor}`,
        p: 2,
        color: theme.palette.text.primary,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h6" sx={{ letterSpacing: 0.5 }}>
          {intl.formatMessage({ id: "dashboard.cameraStatusTitle" })}
        </Typography>
        {headerRight ? (
          <Box sx={{ minWidth: { xs: "100%", sm: 240 }, width: { xs: "100%", sm: "auto" } }}>{headerRight}</Box>
        ) : null}
      </Box>
      {error ? (
        <Alert severity="error" sx={{ mb: cameras.length > 0 ? 2 : 0 }}>{error}</Alert>
      ) : null}
      {!loading && cameras.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          {intl.formatMessage({ id: "dashboard.noCameras" })}
        </Typography>
      ) : (
        <TableContainer>
          <Table
            stickyHeader
            size="small"
            sx={{
              borderCollapse: "separate",
              borderSpacing: 0,
              "& .MuiTableCell-root": { whiteSpace: "nowrap" },
            }}
          >
            <TableHead>
              <TableRow>
                {headers.map(header => (
                  <TableCell
                    key={header}
                    sx={{
                      backgroundColor: headerBg,
                      color: theme.palette.text.primary,
                      borderBottom: `1px solid ${borderColor}`,
                      fontWeight: fontWeights.semiBold,
                    }}
                  >
                    {header}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading
                ? Array.from({ length: 4 }).map((_, index) => (
                    <TableRow
                      key={`skeleton-${index}`}
                      sx={{
                        borderBottom: `1px solid ${borderColor}`,
                        backgroundColor: index % 2 === 1 ? evenRowBg : "transparent",
                      }}
                    >
                      {headers.map((header) => (
                        <TableCell key={header}>
                          <Skeleton width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                : cameras.map((camera, index) => (
                    <TableRow
                      key={camera.id}
                      hover
                      sx={{
                        backgroundColor: index % 2 === 1 ? evenRowBg : "transparent",
                        "&.MuiTableRow-hover:hover": { backgroundColor: rowHoverBg },
                        "& > .MuiTableCell-root": { borderBottom: `1px solid ${borderColor}` },
                      }}
                    >
                      <TableCell>{camera.name}</TableCell>
                      <TableCell>{renderCameraPill(camera.cameraStatus)}</TableCell>
                      <TableCell sx={{ maxWidth: 360, overflow: "hidden" }}>{renderDetail(camera.lastEvent)}</TableCell>
                      <TableCell>{renderDetail(camera.sdCardStatus)}</TableCell>
                      <TableCell>{renderDetail(camera.buttonStatus)}</TableCell>
                    </TableRow>
                  ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default CameraStatusTable;
